import { Table, Modal, TableColumnsType, Button } from "antd";

import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import {
  axiosErrorMessages,
  GetLicenseColumns,
  ManageLicenseColumn,
} from "../../../API/api";
import { MenuOutlined } from "@ant-design/icons";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import type { DragEndEvent } from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import axios, { AxiosError } from "axios";
import { Backdrop, CircularProgress } from "@mui/material";
interface ColumnCustomisation {
  handleClosePopup: (data: boolean) => void;
}
interface DataType {
  columnId: number;
  softwareId: number;
  softwareName: string;
  columnOrder: number;
  key: number;
}

const columns: TableColumnsType<DataType> = [
  {
    key: "sort",
  },
  {
    title: "Name",
    dataIndex: "softwareName",
  },
];

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  "data-row-key": string;
}

const Row = ({ children, ...props }: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if ((child as React.ReactElement).key === "sort") {
          return React.cloneElement(child as React.ReactElement, {
            children: (
              <MenuOutlined
                ref={setActivatorNodeRef}
                style={{ touchAction: "none", cursor: "move" }}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

// const ColumnCustomisation = () => {
const ColumnCustomisation: React.FC<ColumnCustomisation> = ({
  handleClosePopup,
}) => {
  const [licenseColumnList, setLicenseColumnList] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([
    1, 2, 5, 6, 14,
  ]);

  const [orderSortedData, setSortedData] = useState([]);

  useEffect(() => {
    getLicenseColumnList();
  }, []);

  const getLicenseColumnList = async () => {
    try {
      setIsLoading(true);
      const response = await GetLicenseColumns();
      const newData1 = response.data.map((category: any, index: number) => ({
        ...category,
        key: category.softwareId,
        rowKey: index + 1,
      }));

      setLicenseColumnList(
        newData1.sort((a: any, b: any) => a.columnOrder - b.columnOrder)
      );
      setSelectedRowKeys(
        newData1
          .filter((item: any) => item.columnOrder)
          .map((item: any) => item.rowKey)
      );
      setIsLoading(false);
    } catch (error: any) {
      let errorMsg;
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        errorMsg = axiosErrorMessages(axiosError);
      } else {
        errorMsg = error.message;
      }
      enqueueSnackbar(errorMsg, {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
      });
      // enqueueSnackbar("Error in fetching data",{ variant: 'error', anchorOrigin: {
      //   vertical: 'top',
      //   horizontal: 'right',
      // },})
      setIsLoading(false);
    }
  };

  const onClosePopup = () => {
    setIsOpen(false);
    handleClosePopup(false);
  };

  const saveHandler = () => {
    // if(selectedRowKeys.length > 0)  {
    const filteredList = licenseColumnList
      .filter((item: any) => selectedRowKeys.includes(item.rowKey))
      .map((item: any, index: number) => ({
        softwareId: item.softwareId,
        columnOrder: index + 1,
      }));

    setIsLoading(true);
    ManageLicenseColumn(filteredList)
      .then((reponse: any) => {
        enqueueSnackbar("New Columns Updated Successfully", {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        });
        setIsLoading(false);
        onClosePopup();
      })
      .catch((error: any) => {
        let errorMsg;
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          errorMsg = axiosErrorMessages(axiosError);
        } else {
          errorMsg = error.message;
        }
        enqueueSnackbar(errorMsg, {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
        });

        setIsLoading(false);
      });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setLicenseColumnList((previous) => {
        const activeIndex = previous.findIndex(
          (i: any) => i.rowKey === active.id
        );
        const overIndex = previous.findIndex((i: any) => i.rowKey === over?.id);
        return arrayMove(previous, activeIndex, overIndex);
      });
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: any, selectedRows: any) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };
  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme: any) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Modal
        maskClosable={false}
        footer={null}
        title="Column Customisation"
        onCancel={onClosePopup}
        open={isOpen}
      >
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={orderSortedData.map((i: any) => i.key)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              rowSelection={rowSelection}
              components={{
                body: {
                  row: Row,
                },
              }}
              size="small"
              showHeader={false}
              rowKey="rowKey"
              columns={columns}
              pagination={{
                showSizeChanger: false,
                hideOnSinglePage: true,
              }}
              dataSource={licenseColumnList}
            />
          </SortableContext>
        </DndContext>
        <Button
          style={{ marginTop: "10px" }}
          className="pm-btn"
          type="primary"
          htmlType="submit"
          onClick={saveHandler}
        >
          Save
        </Button>
      </Modal>
    </>
  );
};

export default ColumnCustomisation;
